import { _x } from '@wordpress/i18n'
import { useEffect, useMemo, useState } from 'react'
import { useInfiniteHits } from 'react-instantsearch'
import { t } from '../../../../assets/utils/algolia/algoliaLocalization'
import { ServicesUiProvider } from '../context'
import { useDateFilterContext } from '../context/DateFilterContext'
import { groupByDay } from '../utils'
import ServiceFilters from './Filters'
import ServiceList from './List'
import ServiceLocationCard from './LocationCard'
import ServiceTeaser from './Teaser'
import { useConfigContext, useWidgetOptions } from './ConfigContext'

export default function SearchUI() {
  const { items } = useInfiniteHits()
  const { compactView, hideImage, fallbackImageUrl, fallbackImageAlt } =
    useWidgetOptions()

  const {
    toNextWeek,
    toPreviousWeek,
    toToday,
    isToday,
    canGoToPreviousWeek,
    canGoToNextWeek
  } = useDateFilterContext()

  // Compact view spans several weeks, so days must read top-down chronologically
  const groupedItems = useMemo(
    () =>
      groupByDay(items)?.sort((a, b) =>
        compactView ? a.date - b.date : b.date - a.date
      ),
    [items, compactView]
  )

  const firstHit = groupedItems?.[0]?.items?.[0] ?? null

  const [selectedHitId, setSelectedHitId] = useState<string | null>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only way
  useEffect(() => {
    setSelectedHitId(null)
  }, [items])

  const selectedHit = items.find((item) => item.objectID === selectedHitId)

  const serviceSeries = selectedHit?.series

  const { placeholderImageUrl } = useConfigContext()

  // Memoized: a new object on every render would retrigger the effect below endlessly
  // A custom fallback image from the block settings takes precedence over the theme placeholder
  const placeholderImage = useMemo(
    () =>
      fallbackImageUrl
        ? {
            url: fallbackImageUrl,
            title: fallbackImageAlt,
            altText: fallbackImageAlt
          }
        : {
            url: placeholderImageUrl,
            title: 'Faithful God',
            altText: 'Faithful God - Celebration Placeholder'
          },
    [placeholderImageUrl, fallbackImageUrl, fallbackImageAlt]
  )

  const [teaserImage, setTeaserImage] = useState(placeholderImage)
  useEffect(() => {
    const image =
      selectedHit?.image ??
      serviceSeries?.image ??
      firstHit?.image ??
      firstHit?.series?.image ??
      placeholderImage
    setTeaserImage(image)
  }, [
    selectedHit?.image,
    serviceSeries?.image,
    firstHit?.image,
    placeholderImage
  ])

  const locationCardHit = selectedHit ?? firstHit

  return (
    <ServicesUiProvider
      value={{ selectedHit: selectedHitId, selectHit: setSelectedHitId }}
    >
      <section>
        <div className="mx-auto" style={{ maxWidth: '64rem' }}>
          <div className="services-display">
            <div className="card" data-bs-theme="dark">
              {!hideImage && (
                <ServiceTeaser
                  imageUrl={t(teaserImage?.url, { fallback: 'any' }) ?? ''}
                  imageAlt={t(teaserImage?.altText, { fallback: 'any' }) ?? ''}
                  seriesDescription={t(serviceSeries?.description)}
                />
              )}
              <div className="card-body">
                <div className="px-2">
                  <ServiceFilters />
                </div>
                {!compactView && (
                  <div className="d-flex justify-content-between px-2">
                    <button
                      type="button"
                      className="btn services-pagination-btn services-pagination-btn-previous"
                      onClick={toPreviousWeek}
                      aria-label={_x(
                        'Show previous week of services',
                        'Services List Pagination Button Aria Label',
                        'icf'
                      )}
                      style={{
                        visibility: canGoToPreviousWeek() ? 'visible' : 'hidden'
                      }}
                      disabled={!canGoToPreviousWeek()}
                    >
                      <i aria-hidden="true" className="icon fa-chevron-left" />
                      {_x(
                        'Previous week',
                        'Services List Pagination Button',
                        'icf'
                      )}
                    </button>
                    {!isToday() && (
                      <button
                        type="button"
                        className="btn services-pagination-btn services-pagination-btn-today"
                        onClick={toToday}
                        aria-label={_x(
                          'Show current week of services',
                          'Services List Pagination Button Aria Label',
                          'icf'
                        )}
                      >
                        {_x(
                          'This week',
                          'Services List Pagination Button',
                          'icf'
                        )}
                      </button>
                    )}

                    <button
                      type="button"
                      className="btn services-pagination-btn services-pagination-btn-next"
                      onClick={toNextWeek}
                      aria-label={_x(
                        'Show next week of services',
                        'Services List Pagination Button Aria Label',
                        'icf'
                      )}
                      style={{
                        visibility: canGoToNextWeek() ? 'visible' : 'hidden'
                      }}
                      disabled={!canGoToNextWeek()}
                    >
                      {_x('Next week', 'Services List Pagination Button', 'icf')}
                      <i aria-hidden="true" className="icon fa-chevron-right" />
                    </button>
                  </div>
                )}
                <div className="pe-sm-2">
                  <div className="services-list-container ps-2 pe-2 mt-3">
                    <ServiceList groupedItems={groupedItems} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!!locationCardHit && (
            <div className="services-location mt-5">
              <ServiceLocationCard hit={locationCardHit} />
            </div>
          )}
        </div>
      </section>
    </ServicesUiProvider>
  )
}
